import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  NBA_MY_CLUB_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { isType } from '@sorare/core/src/lib/gql';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import { CurrentFixture } from '@sorare/gameplay/src/components/profile/CurrentFixture';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import { RecentPurchases } from '@sorare/gameplay/src/components/profile/RecentPurchases';
import { WatchlistsBlock } from '@sorare/gameplay/src/components/profile/WatchlistsBlock';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import { GalleryOnboarding } from 'components/managerProfile/GalleryOnboarding';

import type {
  NbaMyClubQuery,
  NbaMyClubQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const NBA_MY_CLUB_QUERY = gql`
  query NbaMyClubQuery(
    $user: String!
    $isLoggedIn: Boolean!
    $sport: Sport!
    $firstCardsToDisplay: Int!
  ) {
    so5 {
      so5Fixture(sport: $sport, type: LIVE, eventType: CLASSIC) {
        slug
        ...CurrentFixture_so5Fixture
      }
    }
    currentUser @include(if: $isLoggedIn) {
      slug
    }
    user(slug: $user) {
      slug
      id
      trades(sport: [$sport], first: 20) {
        nodes {
          ...RecentPurchases_tokenOperationTradeInterface
        }
      }
      nbaUserProfile {
        id
        bannerUrl
        clubName
        shieldUrl
        socialPictureFingerprint
        highlightedDeck {
          id
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      cards(
        sport: NBA
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        sorts: [RARITIES]
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      cardCollections(startedOnly: true, first: 10, sport: NBA) {
        nodes {
          id
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
        }
        totalCount
      }
      ...WatchlistsBlock_user
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
      ...CurrentFixture_user
    }
  }
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${CurrentFixture.fragments.so5Fixture}
  ${CurrentFixture.fragments.user}
  ${RecentPurchases.fragments.tokenOperationTradeInterface}
  ${WatchlistsBlock.fragments.user}
` as TypedDocumentNode<NbaMyClubQuery, NbaMyClubQueryVariables>;

const MyClub = ({ slug }: { slug: string }) => {
  const { generateSportContextPath } = useSportContext();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { currentUser, isMe } = useCurrentUserContext();
  const isCurrentUserPage = isMe(slug);
  const { data, loading } = useQuery(NBA_MY_CLUB_QUERY, {
    variables: {
      user: slug || '',
      isLoggedIn: !!currentUser,
      sport: Sport.NBA,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
    },
    skip: !slug,
  });
  const { lifecycle } = useLifecycle();

  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data) {
    return <LoadingIndicator fullHeight />;
  }

  const { user, so5 } = data;
  const { cards, trades } = user;

  const shareProps = {
    managerProfilePath: generatePath(NBA_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.nbaUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
      params: { slug },
    }),
    gallerySocialPictures: data?.user.nbaUserProfile?.gallerySocialPictureUrls,
  };

  const filteredRecentPurchases = (trades.nodes || []).filter(
    transaction =>
      (isType(transaction, 'TokenAuction') ||
        isType(transaction, 'TokenPrimaryOffer')) &&
      !!transaction.anyCards?.length
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={user}
        sport={Sport.NBA}
        userSportProfile={data.user.nbaUserProfile}
        {...shareProps}
      />
    ),
    currentFixture: (
      <CurrentFixture
        so5Fixture={so5.so5Fixture}
        sport={Sport.NBA}
        user={user}
      />
    ),
    recentPurchases: (
      <RecentPurchases
        userSlug={user.slug}
        transactions={filteredRecentPurchases.slice(0, 5)}
      />
    ),
    watchlists: <WatchlistsBlock user={user} />,
    gallery: (
      <>
        <Gallery
          sport={Sport.NBA}
          cards={cards.nodes}
          onboardingComponent={isCurrentUserPage ? <GalleryOnboarding /> : null}
          shouldShowOnboarding={!lifecycle?.[LIFECYCLE.sawGalleryOnboarding]}
          highlightedDeck={data.user.nbaUserProfile?.highlightedDeck}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.NBA}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
  };

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={data?.user.nbaUserProfile?.bannerUrl}>
        <Grid>
          {blocks.header}
          {blocks.gallery}
          {blocks.watchlists}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={data?.user.nbaUserProfile?.bannerUrl}>
      <NewGrid>
        <Main>
          <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
        </Main>
        <Aside>
          <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
          {blocks.watchlists}
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

const MyClubPage = () => {
  const { slug } = useTypedParams<PageParams>();

  return <MyClub slug={slug} />;
};

export default MyClubPage;
